/**
 * FORTRESS - Visitor Analytics Hook
 * ==================================
 * Automatically tracks page views and sessions.
 * Works across web, Android, and iOS platforms.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('tanzeen_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('tanzeen_visitor_id', visitorId);
  }
  return visitorId;
};

// Detect platform
const getPlatform = () => {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
  } catch {}
  return 'web';
};

// Track page view
const trackPageView = async (page) => {
  try {
    await fetch(`${API_URL}/api/fortress/analytics/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        visitor_id: getVisitorId(),
        platform: getPlatform(),
        referrer: document.referrer || null,
        user_agent: navigator.userAgent
      })
    });
  } catch (err) {
    // Silent fail - don't disrupt user experience
    console.debug('Analytics tracking failed:', err);
  }
};

// Track session start
const trackSessionStart = async () => {
  try {
    await fetch(`${API_URL}/api/fortress/analytics/track/session?platform=${getPlatform()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (err) {
    console.debug('Session tracking failed:', err);
  }
};

/**
 * useAnalytics Hook
 * Automatically tracks page views on route changes
 */
export const useAnalytics = () => {
  const location = useLocation();
  const hasTrackedSession = useRef(false);

  // Track session start once on mount
  useEffect(() => {
    if (!hasTrackedSession.current) {
      hasTrackedSession.current = true;
      trackSessionStart();
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    const page = location.pathname;
    trackPageView(page);
  }, [location.pathname]);
};

/**
 * AnalyticsProvider Component
 * Wrap your app with this to enable automatic tracking
 */
export const AnalyticsProvider = ({ children }) => {
  useAnalytics();
  return children;
};

export default useAnalytics;
